import PropTypes from "prop-types";
import { navigate } from "gatsby";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";

import { CartContext } from "../../context/CartProvider";
import { DiscountContext } from "../../context/DiscountProvider";
import { HiddenElementsContext } from "../../context/HiddenElements";
import StoreContext from "../../context/store";
import { useCurrentCountryCode } from "../../hooks/usePrices";
import { LuxeContext, useAddToCartWithLuxe } from "../../context/LuxeProvider";
import { queryProductData, useProductData2 } from "../../context/products";

import { InventoryPolicies } from "../../constants/InventoryPolicies";
import { HandleType } from "../../constants/HandleType";
import TAG from "../../constants/Tag";

const ACTION = {
  OPEN_SIDECART: "OPEN_SIDECART",
  ADD_TO_CART: "ADD_TO_CART",
  NAVIGATE: "NAVIGATE",
  SET_HEIGHT: "SET_HEIGHT",
  TOGGLE_LUXE: "TOGGLE_LUXE",
  SCROLL_TO: "SCROLL_TO",
  RETRIEVE_PRODUCTS: "RETRIEVE_PRODUCTS",
  GET_CART: "GET_CART",
  REMOVE_FROM_CART: "REMOVE_FROM_CART",
};

const DEFAULT_HEIGHT = 1000;

const DISCOUNT = {
  VALUE: 5,
  CODE: "FLASHSALE",
};

function sendCartToIframe(eventSource, iframeOrigin, cartToSend) {
  eventSource.postMessage({ action: ACTION.GET_CART, params: cartToSend }, iframeOrigin);
}

function IframeBlock({ content }) {
  const { contentUrl, title, shouldHideMenu, customHeight } = content;
  const iframeOrigin = new URL(contentUrl).origin;
  const { setDisplayNavigation, setDisplayAB } = useContext(StoreContext);
  const { setIsSideCartOpen, cart, resetCart, removeSingleProductsFromCart } =
    useContext(CartContext);
  const { setFinalDiscount, discountInfo } = useContext(DiscountContext);
  const { show, hide } = useContext(HiddenElementsContext);
  const { luxe, setLuxeActive } = useContext(LuxeContext);

  const [height, setHeight] = useState(0);
  const countryCode = useCurrentCountryCode();
  const addToCartWithLuxe = useAddToCartWithLuxe();

  const [handles, setHandles] = useState([]);
  const productsData = useProductData2(handles, "long", countryCode, "en-US");
  const totalItems = useMemo(
    () =>
      cart.lines.filter(
        (line) =>
          ![HandleType.ORDER_PROTECTION, HandleType.SUBSCRIPTION].includes(
            line.variant?.product?.handle
          )
      ).length,
    [cart.lines]
  );

  useEffect(() => {
    resetCart();
  }, []);

  useEffect(() => {
    if (totalItems > 1 && discountInfo[0].code === DISCOUNT.CODE) {
      setFinalDiscount({});
    } else if (totalItems < 2 && discountInfo[0].code !== DISCOUNT.CODE) {
      setFinalDiscount({
        code: DISCOUNT.CODE,
        discounts: [[1, DISCOUNT.VALUE]],
        shouldCodeBeApplied: true,
        isLuxe: false,
        matchType: "exclude",
        matchTags: [],
      });
    }
  }, [cart.lines, discountInfo.code, setFinalDiscount, totalItems]);

  const addVariantToCart = useCallback(
    async ({ handle, variantId, shouldOpenSideCart, shouldGoToCheckout = false }) => {
      const fullVariantId = `gid://shopify/ProductVariant/${variantId}`;
      const productData =
        productsData.data?.find((p) => p.handle === handle) ||
        (await queryProductData([handle], "long", countryCode, "en-US"))[0];

      const {
        id: productId,
        title: productTitle,
        productType,
        variants,
        tags,
      } = productData.shopify;
      const variant = variants.find((v) => v.id === fullVariantId);

      const dataProductToAdd = {
        quantity: 1,
        attributes: [{ key: "_lp_paid", value: "true" }],
        variant: {
          id: fullVariantId,
          sku: variant.sku,
          price: variant.price,
          compareAtPrice: variant.compareAtPrice,
          product: {
            title: productTitle,
            id: productId,
            handle,
            productType,
          },
        },
      };

      const isPreOrder =
        productData.contentful.node.isPreOrder &&
        variant.inventoryPolicy === InventoryPolicies.CONTINUE;
      const shippingDate = isPreOrder
        ? (() => {
            try {
              const parsedDate = parseISO(productData.contentful.node?.preOrder?.shippingDate);
              return format(parsedDate, "MMM d, yyyy");
            } catch (error) {
              console.error(error);
              return null;
            }
          })()
        : null;
      if (isPreOrder) {
        dataProductToAdd.attributes.push({ key: "_pre_order", value: "true" });
        dataProductToAdd.attributes.push({
          key: "_ships_by_pre_order",
          value: shippingDate,
        });
      }
      const isFinalSale = tags?.includes(TAG.FINAL_SALE);
      if (isFinalSale) {
        dataProductToAdd.attributes.push({ key: "_final_sale", value: "true" });
      }

      const newCart = await addToCartWithLuxe({
        dataProductsToAdd: [dataProductToAdd],
        shouldOpenSideCart,
        shouldApplyOrderProtection: true,
        discountCode: totalItems + 1 > 1 && discountInfo[0].code === DISCOUNT.CODE ? "" : null,
      });
      if (newCart && shouldGoToCheckout) {
        window.location.href = newCart.checkoutUrl;
      }
      return newCart;
    },
    [addToCartWithLuxe, countryCode, discountInfo[0].code, productsData.data, totalItems]
  );

  const removeLineItemFromCart = useCallback(
    async ({ lineItemId, shouldOpenSideCart }) => {
      const lineToRemove = cart.lines.find((line) => line.id.startsWith(lineItemId));
      if (!lineToRemove) return;

      await removeSingleProductsFromCart({
        lineToRemove,
        shouldOpenSideCart,
        discountCode:
          totalItems - 1 < 2 && discountInfo[0].code !== DISCOUNT.CODE ? DISCOUNT.CODE : null,
      });
    },
    [cart.lines, discountInfo[0].code, removeSingleProductsFromCart, totalItems]
  );

  const toggleLuxe = useCallback(
    (params) => {
      const currentLuxe = luxe.shouldAddLuxeInNextATC || luxe.hasLuxeInCart;
      setLuxeActive(
        params !== undefined ? params === "true" : !currentLuxe,
        luxe.subscriptionPlan,
        cart,
        false
      );
    },
    [cart, luxe.hasLuxeInCart, luxe.shouldAddLuxeInNextATC, luxe.subscriptionPlan, setLuxeActive]
  );

  useEffect(() => {
    // On cart update, useEffect will be called again and we need to send the cart to the iframe
    sendCartToIframe(document.getElementById(contentUrl).contentWindow, iframeOrigin, {
      ...cart,
      lines: cart.lines.filter(
        (line) =>
          ![HandleType.ORDER_PROTECTION, HandleType.SUBSCRIPTION].includes(
            line.variant?.product?.handle
          )
      ),
    });
  }, [cart, contentUrl, iframeOrigin]);

  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.origin === iframeOrigin) {
        const { action, params } = event.data;

        switch (action) {
          case ACTION.OPEN_SIDECART:
            setIsSideCartOpen(true);
            break;
          case ACTION.ADD_TO_CART:
            await addVariantToCart(params);
            break;
          case ACTION.REMOVE_FROM_CART:
            await removeLineItemFromCart(params);
            break;
          case ACTION.NAVIGATE:
            navigate(params);
            break;
          case ACTION.SET_HEIGHT:
            if (params !== height) {
              setHeight(params);
            }
            break;
          case ACTION.RETRIEVE_PRODUCTS:
            setHandles((currentHandles) => {
              const uniqueHandles = Array.from(new Set(params));
              if (uniqueHandles.join() !== currentHandles.join()) {
                return uniqueHandles;
              }
              return currentHandles;
            });
            break;
          case ACTION.TOGGLE_LUXE:
            toggleLuxe(params);
            break;
          case ACTION.SCROLL_TO:
            window.scrollTo(params);
            break;
          case ACTION.GET_CART:
            sendCartToIframe(event.source, iframeOrigin, cart);
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener("message", handleMessage);
    // We ask the height to the iframe as addEventListener
    // is often call after the original SET_HEIGHT from the iframe
    if (!height) {
      document.getElementById(contentUrl).contentWindow.postMessage("REQUEST_HEIGHT", iframeOrigin);
    }
    if (!handles.length) {
      document
        .getElementById(contentUrl)
        .contentWindow.postMessage("REQUEST_PRODUCTS", iframeOrigin);
    }

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [
    addVariantToCart,
    cart,
    contentUrl,
    handles.length,
    height,
    iframeOrigin,
    removeLineItemFromCart,
    setIsSideCartOpen,
    toggleLuxe,
  ]);

  useEffect(() => {
    if (shouldHideMenu) {
      setDisplayNavigation(false);
      setDisplayAB(false);
      hide();
      return () => {
        setDisplayNavigation(true);
        setDisplayAB(true);
        show();
      };
    }
  }, [setDisplayNavigation, shouldHideMenu, setDisplayAB, hide, show]);

  let customStyle = { border: "none" };
  if (customHeight && customHeight !== "null") {
    customStyle = { ...customStyle, height: customHeight };
  }

  return (
    <iframe
      id={contentUrl}
      src={contentUrl}
      style={customStyle}
      title={title}
      width="100%"
      height={`${height || DEFAULT_HEIGHT}px`}
    />
  );
}

IframeBlock.propTypes = {
  content: PropTypes.shape({
    contentUrl: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    shouldHideMenu: PropTypes.bool.isRequired,
    customHeight: PropTypes.string,
  }).isRequired,
};

export default IframeBlock;
